import { shouldShowKaffeesteuer } from "@bcc/components/finance/utils/kaffeesteuer";
import { calculatePaymentinfo } from "@client-shared/providers/payout";
import type { PayoutContextState } from "@client-shared/providers/payout";
import { useVendorContext } from "@client-shared/providers/vendor";
import { useMemo } from "react";
import { useDemoDataModule } from "./use-demo-data-module";

const loadInvoicesDemo = () => import("./invoices.demo");

const FALLBACK_CURRENCY_CODE = "USD";

const noop = (): void => undefined;

/**
 * Payout context carrying sample invoices while the tour runs, to wrap the invoices view in;
 * undefined in normal use. The date filter and per-order item fetch are served from the samples.
 */
export function useDemoPayoutContext(): PayoutContextState | undefined {
  const { isActive, module } = useDemoDataModule(loadInvoicesDemo);
  const [{ currentVendor, currencyObject }] = useVendorContext();
  const currencyCode = currencyObject?.iso ?? FALLBACK_CURRENCY_CODE;
  const shipToKey = (currentVendor?.properties?.shipTo ?? []).join(";");
  const shipToCountries = useMemo(
    () => (shipToKey ? shipToKey.split(";") : [currentVendor?.countryCode ?? ""]),
    [shipToKey, currentVendor?.countryCode]
  );
  const includeKaffeesteuer = shouldShowKaffeesteuer(currentVendor);

  return useMemo(() => {
    if (!isActive) return undefined;
    const invoices = module?.buildDemoInvoices({ currencyCode, shipToCountries, includeKaffeesteuer }) ?? [];
    return [
      {
        invoices,
        isInitialized: true,
        isLoading: !module,
        invoiceStats: calculatePaymentinfo(invoices),
      },
      noop,
      {
        initialize: noop,
        fetchInvoicesWithDateRange: () => Promise.resolve(),
        fetchPurchaseOrderLineDetails: (purchaseOrderId: string) =>
          Promise.resolve(module?.buildDemoLineDetails(invoices, purchaseOrderId) ?? []),
      },
    ];
  }, [isActive, module, currencyCode, shipToCountries, includeKaffeesteuer]);
}
